import { useCallback, useState, type MouseEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { fmtDate, fmtTime } from '../lib/format';
import type { Comment } from '../lib/types';
import { useLightbox, useToast } from './feedback';
import { Avatar, Button } from './ui';

/**
 * Corpo do relatório renderizado pelo Worker (mesma marcação da rota /r/visit e do PDF).
 * Cliques em fotos abrem o lightbox do app.
 */
export function ReportHtml({ html, onDeletePhoto }: { html: string; onDeletePhoto?: (photoId: string) => Promise<void> }) {
  const lightbox = useLightbox();
  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-full]');
      if (!el) return;
      e.preventDefault();
      const d = el.dataset;
      const id = d.photo ?? '';
      lightbox({
        id, url: d.full!, setor: d.setor ?? '', legenda: d.legenda || null, isNc: !!d.nc, stamp: d.stamp ?? '', author: d.author ?? '',
        onDelete: onDeletePhoto && id ? () => onDeletePhoto(id) : undefined,
      });
    },
    [lightbox, onDeletePhoto],
  );
  return <div onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** CSS do relatório vem do servidor uma vez (mesmo arquivo do PDF). */
export function useReportCss() {
  return useQuery({
    queryKey: ['report-css'],
    queryFn: async () => {
      const res = await fetch('/r/report.css');
      return res.ok ? res.text() : '';
    },
    staleTime: Infinity,
  });
}

export function Comments({ base, canComment }: { base: string; canComment: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const q = useQuery({ queryKey: ['comments', base], queryFn: () => api.get<Comment[]>(`${base}/comments`) });
  const add = useMutation({
    mutationFn: () => api.post(`${base}/comments`, { texto: text.trim() }),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['comments', base] });
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const list = q.data ?? [];
  if (!canComment && list.length === 0) return null;
  return (
    <section className="card lg">
      <div className="section-head"><h2 className="h2">Comentários</h2><span className="counter-mono">{list.length}</span></div>
      {canComment && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) add.mutate();
          }}
          style={{ marginBottom: list.length ? 12 : 0 }}
        >
          <textarea className="textarea" rows={2} placeholder="Escreva um comentário sobre o relatório" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <Button size="sm" variant="dark" type="submit" loading={add.isPending} disabled={!text.trim()}>Comentar</Button>
          </div>
        </form>
      )}
      {list.map((c) => (
        <div key={c.id} className="row gap-10" style={{ alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid var(--divider-2)' }}>
          <Avatar name={c.autorNome} size={28} />
          <div className="flex1">
            <div style={{ fontSize: 12.5 }}><strong style={{ fontWeight: 500 }}>{c.autorNome}</strong> <span className="mono small">{fmtDate(c.createdAt)} {fmtTime(c.createdAt)}</span></div>
            <div style={{ fontSize: 13.5, lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.texto}</div>
          </div>
        </div>
      ))}
    </section>
  );
}
